import { createAsyncThunk } from "@reduxjs/toolkit";
import { serializeError } from "../../app/shared";
import { request, requestForDownloads } from "../../app/api";
import { isNil, omitBy } from "lodash/fp";
import FileModel from "../../models/FileModel";

const SLICE_NAME = "files";

export interface UploadFileInterface {
  file: File,
  locationId?: number[] | null,
  buildingId?: number[] | null,
  licenceId?: number | null,
}

export const uploadFile = createAsyncThunk(
  `${SLICE_NAME}/upload`,
  async ({
    file,
    licenceId,
    locationId,
    buildingId,
  }: UploadFileInterface, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      let url;

      if (locationId) {
        url = `/file/upload?locations[]=${locationId.join(',')}`;
      }

      if (buildingId) {
        url = `/file/upload?buildings[]=${buildingId.join(',')}`;
      }

      if (licenceId) {
        url = `/file/upload?licenceId=${licenceId}`;
      }

      const fileModel: FileModel = await request.post(
        url,
        formData
      );

      return fileModel;
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const getFilesByQuery = createAsyncThunk(
  `${SLICE_NAME}/loadByQuery`,
  async ({
    byLicenceId,
    byLocationId,
    byBuildingId
  }: { byLocationId?: number | null, byBuildingId?: number | null, byLicenceId?: number | null }, { rejectWithValue }) => {
    try {
      const fileList: FileModel[] = await request.get(`/file`, {
        params: omitBy(isNil, { byLocationId, byBuildingId, byLicenceId }),
      });
      return fileList;
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);


export interface EditFileInterface {
  id: number,
  locations?: number[] | null,
  buildings?: number[] | null,
}

export const editFile = createAsyncThunk(
  `${SLICE_NAME}/edit`,
  async ({ id, ...data }: EditFileInterface, { rejectWithValue }) => {
    try {
      const fileModel: FileModel = await request.put(
        `/file/${id}`,
        data
      );

      return fileModel;
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const downloadFile = createAsyncThunk(
  `${SLICE_NAME}/download`,
  async (id: number, { rejectWithValue }) => {
    try {
      requestForDownloads.get(`/file/${id}`, {
        responseType: 'blob',
        withCredentials: true,
      })
        .then((response) => {
          // create file link in browser's memory
          const href = URL.createObjectURL(response.data);
          const attachment = response.headers['attachment-name'];

          // create "a" HTML element with href to file & click
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', attachment); // or any other extension
          document.body.appendChild(link);
          link.click();

          // clean up "a" element & remove ObjectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        })
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const deleteFile = createAsyncThunk(
  `${SLICE_NAME}/delete`,
  async (id: number, { rejectWithValue }) => {
    try {
      await request.delete(`/file/${id}`);
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
)